import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { ENTITY_DATA_SELECTION, ENTITY_DATA_SELECTION_MUTATION } from "./entity-fragments";
import type { FieldFormat } from "@/lib/field-formatter";

export const DATA_TYPE_VALUES = [
  "INT",
  "FLOAT",
  "OBJECT",
  "DATE",
  "DATETIME",
  "TIME",
  "BOOLEAN",
  "TEXT",
  "LONGTEXT",
  "RICHTEXT",
  "JSON",
  "PRICE",
  "PERCENT",
  "ENUM",
  "SEQUENCE",
] as const;
// Mutations return raw entity_fields rows, which the generic ModelService path
// cannot annotate with the registry flags — use the flag-free selection there.
const entityMutationDataStr = ENTITY_DATA_SELECTION_MUTATION;
export type DataTypeEnum = (typeof DATA_TYPE_VALUES)[number];

export const FORM_TYPE_VALUES = [
  "EMAIL",
  "PHONE",
  "WEBSITE",
  "CURRENCY",
  "PERCENT",
  "DURATION",
] as const;

export type FormTypeEnum = (typeof FORM_TYPE_VALUES)[number];

export type RelationType = "RELATION" | "MEDIA";
export type EntityRelationType = "OneToOne" | "OneToMany" | "ManyToOne" | "ManyToMany";

export interface EntityRelationInput {
  id?: string;
  type: EntityRelationType;
  field_name: string;
  target_field_name?: string;
  target_entity: {
    id: string;
    name?: string; // Optional: for display purposes when creating new relations
  };
  media_download_is_public: boolean;
  media_allowed_mime_types: Array<string>;
  entity_is_tightly_coupled: boolean;
  target_entity_is_tightly_coupled: boolean;
}

export interface EnumValueInput {
  id?: string;
  value: string;
}

export interface CreateEntityField {
  name: string;
  data_type: DataTypeEnum;
  default_value?: string;
  validation_string?: string;
  form_type?: FormTypeEnum | null;
  format?: FieldFormat | null;
  formula?: string | null;
  variant_default_entity_instance_id?: string;
  variant_entity_field_set?: Partial<CreateEntityField & { id: string }>;
  entity_field_enum_values_set?: EnumValueInput[];
  entity_sequence_set?: { id: string };
}

export interface UpdateEntityField extends CreateEntityField {
  id: string;
  entity_field_enum_values_add?: EnumValueInput[];
  entity_field_enum_values_remove?: Array<{ id: string }>;
}

const createEntityMutation = gql`
  mutation CreateEntityMutation($input: CreateEntityInput!) {
    createEntity(input: $input) {
      ${entityMutationDataStr}
    }
  }
`;

export const createEntity = async ({
  entityName,
  entityFields,
  entityRelations,
  isAro,
}: {
  entityName: string;
  entityFields: Array<CreateEntityField>;
  entityRelations: Array<EntityRelationInput>;
  isAro: boolean;
}) => {
  const variables = {
    input: {
      is_aro: !!isAro,
      name: entityName,
      ...(entityFields.length > 0 && { entity_fields_add: entityFields }),
      ...(entityRelations.length > 0 && {
        entity_relations_add: entityRelations.map((relation) => ({
          ...("id" in relation && { id: relation.id }),
          type: relation.type,
          field_name: relation.field_name,
          // Always send target_field_name (empty string resets it on the backend)
          target_field_name: relation.target_field_name ?? "",
          // Only send id to backend (EntityInput expects { id })
          target_entity: { id: relation.target_entity.id },
          media_allowed_mime_types: relation.media_allowed_mime_types || [],
          media_download_is_public: !!relation.media_download_is_public,
          entity_is_tightly_coupled: !!relation.entity_is_tightly_coupled,
          target_entity_is_tightly_coupled: !!relation.target_entity_is_tightly_coupled,
        })),
      }),
    },
  };

  const client = createGraphQLClient({ endpointName: "createEntity" });

  const result = (await client.request(createEntityMutation, variables)) as {
    createEntity: {
      data: Entity;
    };
  };

  return result.createEntity.data;
};

const updateEntityMutation = gql`
  mutation UpdateEntityMutation($input: UpdateEntityInput!) {
    updateEntity(input: $input) {
      ${entityMutationDataStr}
    }
  }
`;

export const updateEntity = async ({
  entityId,
  entityName,
  entityFieldsToCreateOrUpdate,
  entityFieldIdsToDelete,
  entityRelationsToCreateOrUpdate,
  entityRelationIdsToDelete,
  isAro,
}: {
  entityId: string;
  entityName?: string;
  entityFieldsToCreateOrUpdate: Array<UpdateEntityField | CreateEntityField>;
  entityFieldIdsToDelete: Array<string>;
  entityRelationsToCreateOrUpdate: Array<EntityRelationInput>;
  entityRelationIdsToDelete: Array<string>;
  isAro?: boolean;
}) => {
  const entityFieldsWithCorrectDataType = entityFieldsToCreateOrUpdate.map((field) => ({
    ...("id" in field && { id: field.id }),
    validation_string: field.validation_string,
    default_value: field.default_value,
    name: field.name,
    form_type: field.form_type,
    format: field.format,
    ...(field.formula != null && { formula: field.formula }),
    // If data_type changed, allow sending it for existing fields
    // Backend should ignore if unchanged or not permitted
    ...(field.data_type && { data_type: field.data_type.toUpperCase() }),
    ...(field.variant_default_entity_instance_id && {
      variant_default_entity_instance_id: field.variant_default_entity_instance_id,
    }),
    ...(field.variant_entity_field_set && {
      variant_entity_field_set: field.variant_entity_field_set,
    }),
    ...(field.entity_field_enum_values_set && {
      entity_field_enum_values_set: field.entity_field_enum_values_set,
    }),
    ...(field.entity_sequence_set && { entity_sequence_set: field.entity_sequence_set }),
    ...("entity_field_enum_values_add" in field &&
      (field as UpdateEntityField).entity_field_enum_values_add && {
        entity_field_enum_values_add: (field as UpdateEntityField).entity_field_enum_values_add,
      }),
    ...("entity_field_enum_values_remove" in field &&
      (field as UpdateEntityField).entity_field_enum_values_remove && {
        entity_field_enum_values_remove: (field as UpdateEntityField)
          .entity_field_enum_values_remove,
      }),
  }));

  const entityFieldsToDelete = entityFieldIdsToDelete.map((id) => ({ id }));

  const variables = {
    input: {
      id: entityId,
      // Only send fields the caller actually changed — every input but the id is
      // optional on the backend, so unchanged parts are omitted.
      ...(entityName !== undefined && { name: entityName }),
      ...(isAro !== undefined && { is_aro: isAro }),
      ...(entityFieldsWithCorrectDataType.length > 0 && {
        entity_fields_add: entityFieldsWithCorrectDataType,
      }),
      ...(entityFieldsToDelete.length > 0 && {
        entity_fields_remove: entityFieldsToDelete,
      }),
      ...(entityRelationsToCreateOrUpdate.length > 0 && {
        entity_relations_add: entityRelationsToCreateOrUpdate.map((relation) => ({
          ...("id" in relation && { id: relation.id }),
          type: relation.type,
          field_name: relation.field_name,
          // Always send target_field_name (empty string resets it on the backend)
          target_field_name: relation.target_field_name ?? "",
          // Only send id to backend (EntityInput expects { id })
          target_entity: { id: relation.target_entity.id },
          media_allowed_mime_types: relation.media_allowed_mime_types || [],
          media_download_is_public: !!relation.media_download_is_public,
          entity_is_tightly_coupled: !!relation.entity_is_tightly_coupled,
          target_entity_is_tightly_coupled: !!relation.target_entity_is_tightly_coupled,
        })),
      }),
      ...(entityRelationIdsToDelete.length > 0 && {
        entity_relations_remove: entityRelationIdsToDelete.map((id) => ({
          id,
        })),
      }),
    },
  };

  const client = createGraphQLClient({ endpointName: "updateEntity" });

  const result = (await client.request(updateEntityMutation, variables)) as {
    updateEntity: {
      data: Entity;
    };
  };

  return result.updateEntity.data;
};

const entityListQuery = gql`
  query entityList {
    entityList(pagination: { page: 1, per_page: 1000 }) {
      ${ENTITY_DATA_SELECTION}
    }
  }
`;

export interface EntityRelated {
  id: string;
  type: string;

  field_name: string;
  target_field_name?: string;
  entity: {
    data: {
      id: string;
      name: string;
    };
  };
  target_entity: {
    data: {
      id: string;
      name: string;
    };
  };
  created_at: string;
  media_download_is_public: boolean;
  media_allowed_computed_mime_types: Array<string>;
  media_allowed_computed_extentions: Array<string>;
  media_allowed_mime_types: Array<string>;
  entity_is_tightly_coupled: boolean;
  target_entity_is_tightly_coupled: boolean;
  updated_at: string;
}

export interface EntityField {
  id: string;
  name: string;
  data_type: DataTypeEnum;
  // Registry flags mirroring GraphQL SDL inclusion: is_readable === exposed as a
  // queryable scalar (FK/internal columns are false). Used to build the entity
  // schema from the registry instead of introspection.
  is_readable?: boolean;
  is_writeable?: boolean;
  form_type?: FormTypeEnum;
  // unvalidated as loaded; read it through parseFieldFormat
  format?: FieldFormat | null;
  // Set on formula fields only; printed from the backend AST with current names.
  formula?: string | null;
  validation_string?: string;
  variant_default_entity_instance_id?: string;
  variant_entity_field?: {
    data: {
      id: string;
    };
  };
  entity_field_enum_values?: {
    data: Array<{ id: string; value: string }>;
  };
  entity_sequence?: {
    data: { id: string; name: string; locked: boolean } | null;
  };
}

export interface Entity {
  id: string;
  name: string;
  is_system_entity: boolean;
  is_aro: boolean;
  /** Whether the served schema has a top-level `<entity>List` / `<entity>ById`. Read path only. */
  can_list?: boolean;
  can_get_by_id?: boolean;
  entity_relations?: {
    data: Array<EntityRelated>;
  };
  entity_related?: {
    data: Array<EntityRelated>;
  };
  entity_fields?: {
    data: Array<EntityField>;
  };
}

export const getEntityList = async () => {
  const client = createGraphQLClient({ endpointName: "entityList" });

  const entities = (await client.request(entityListQuery, {})) as {
    entityList: {
      data: Array<Entity>;
    };
  };

  return entities.entityList.data;
};

// Its own small `me` request: useMyUser's waits for the entity list and is rarely refetched.
const schemaHashQuery = gql`
  query schemaHash {
    me {
      schema_hash
    }
  }
`;

export const getSchemaHash = async () => {
  const client = createGraphQLClient({ endpointName: "schemaHash" });
  const result = (await client.request(schemaHashQuery, {})) as {
    me: { schema_hash: string };
  };
  return result.me.schema_hash;
};

const deleteEntitiesMutation = gql`
  mutation DeleteEntitiesMutation($ids: [ID!]!) {
    deleteEntity(ids: $ids) {
      data {
        success
      }
    }
  }
`;

export const deleteEntities = async (entityIds: string[]) => {
  try {
    const client = createGraphQLClient({ endpointName: "deleteEntity" });

    const result = (await client.request(deleteEntitiesMutation, {
      ids: entityIds,
    })) as {
      deleteEntity: {
        data: {
          success: boolean;
        };
      };
    };

    return result.deleteEntity.data?.success;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export interface FormulaPreview {
  formula: string | null;
  error: { message: string; position: number | null } | null;
  samples: Array<{ id: string; label: string; value: number | null; picked: boolean }>;
}

export const previewFormula = async (
  entityName: string,
  formula: string,
  fieldId?: string,
  instanceIds: string[] = [],
): Promise<FormulaPreview> => {
  const result = (await createGraphQLClient({ endpointName: "entityFieldFormulaPreview" }).request(
    gql`
      query entityFieldFormulaPreview(
        $entityName: String!
        $formula: String!
        $fieldId: ID
        $instanceIds: [ID!]
      ) {
        entityFieldFormulaPreview(
          entityName: $entityName
          formula: $formula
          fieldId: $fieldId
          instanceIds: $instanceIds
        ) {
          formula
          error {
            message
            position
          }
          samples {
            id
            label
            value
            picked
          }
        }
      }
    `,
    { entityName, formula, fieldId, instanceIds },
  )) as { entityFieldFormulaPreview: FormulaPreview };
  return result.entityFieldFormulaPreview;
};
